import _ from "lodash";
import moment from "moment";

import FirestoreDocument from "./document";
import Snapshot from "../snapshot";
import Method from "../method";
import TimeUtils from "utils/time";
import Logger from "utils/logger";

export default class Snapshots extends FirestoreDocument {
  static COLLECTION = "snapshots";

  constructor(id, data = {}) {
    super();
    this.id = id;
    this.resetWith(data);
  }

  async fetch() {
    try {
      const { id, data } = await super.fetch();
      this.id = id;
      this.setRaw(data);
    } catch (error) {
      Logger.error("Error fetching snapshots:", error);
      // No snapshot exists for user, so reset data
      this.resetWith({});
    }
  }

  get gaps() {
    const imageDates = _.map(this.images, (img) =>
      moment(img.date).startOf("month").format(TimeUtils.FullDateString)
    );
    const timelineDates = _.keys(this.raw);
    return _.map(_.difference(imageDates, timelineDates), (d) =>
      moment(d).format("MMM YYYY")
    );
  }

  get heatmap() {
    const sumsByDate = {};
    const thisMonth = moment().startOf("month");
    const dates = _.get(
      this.raw,
      [thisMonth.format(TimeUtils.FullDateString), "dates"],
      {}
    );
    for (
      var m = thisMonth.clone().startOf("month").startOf("week");
      m.isBefore(thisMonth.clone().endOf("month").endOf("week"));
      m.add(1, "days")
    ) {
      const dateKey = m.format("YYYY-MM-DD");
      sumsByDate[dateKey] = {
        date: dateKey,
        day: m.format("ddd"), // day of the week
        duration: dates[dateKey] || 0, // Use duration from dates map or 0
      };
    }
    return Object.values(sumsByDate);
  }

  get json() {
    const data = {};
    _.forEach(this.raw, (v, k) => (data[k] = v.json));
    data["total"] = this.total.json;
    return data;
  }

  async methodsBreakdown(user) {
    const total = _.sum(_.values(this.total.methods));

    // Aggregate data
    const categories = {};
    const methods = {};
    // TODO(perf): await-in-loop. Fast now that Methods.getInstance() is a cached
    // singleton (each call resolves in-memory), but still N awaits -- could resolve
    // the catalog once up front and do plain synchronous lookups here.
    for (const [methodName, duration] of Object.entries(this.total.methods)) {
      const officialName = await Method.getOfficialName(methodName);
      const category = await Method.getCategory(methodName);

      if (!_.has(categories, category)) {
        categories[category] = 0;
      }
      if (!_.has(methods, officialName)) {
        methods[officialName] = {
          name: officialName,
          category: category,
          value: 0,
          total: 0,
        };
      }
      categories[category] += duration;
      methods[officialName].value += duration;
      methods[officialName].total = total;
    }

    // Transform data
    const reformattedCategories = _.map(categories, (value, key) => ({
      name: key,
      value: value,
      total: total,
    }));

    return {
      categories: reformattedCategories,
      methods: _.values(methods),
    };
  }

  async methodsOverTime(user) {
    const allPotentialDates = _.concat(_.keys(this.raw), this.gaps);
    const firstYear = moment(_.first(_.sortBy(allPotentialDates))).year();
    const listOfMonths = TimeUtils.getAllMonthsSince(firstYear);

    // Reformat data
    const results = [];
    // TODO(perf): nested await-in-loop (month x method). Fast now via the cached
    // Methods singleton, but ideally resolve the catalog once and map synchronously.
    for (const month of listOfMonths) {
      const methods = _.get(this.raw, [month, "methods"], {});
      const categories = {};

      for (const [methodName, duration] of Object.entries(methods)) {
        const category = await Method.getCategory(methodName);
        if (!_.has(categories, category)) {
          categories[category] = 0;
        }
        categories[category] += duration;
      }

      results.push({
        x: moment(month).format("MMM YYYY"),
        ...categories,
      });
    }

    return results;
  }

  monthHasData(formattedDate) {
    return !!_.get(this.raw, [formattedDate, "sessions"], 0);
  }

  monthData(date, subtract = 0) {
    const month = moment(date)
      .subtract(subtract, "month")
      .startOf("month")
      .format(TimeUtils.FullDateString);
    if (!this.monthHasData(month)) {
      return new Snapshot();
    }
    return this.raw[month];
  }

  resetWith(data = {}) {
    this.raw = {};
    this.images = [];
    this.setRaw(data);
    this.saveCheckpoint();
  }

  setRaw(data) {
    this.raw = {};
    _.forEach(data, (v, k) => {
      this.raw[k] = new Snapshot(v);
    });
  }

  timeline(aggregated) {
    const trackedDates = _.keys(this.raw);
    const imageDates = _.map(this.images, (img) =>
      moment(img.date).startOf("month").format(TimeUtils.FullDateString)
    );
    const allPotentialDates = _.concat(trackedDates, imageDates);
    const firstYear = moment(_.first(_.sortBy(allPotentialDates))).year();
    const listOfMonths = TimeUtils.getAllMonthsSince(firstYear);
    const months = {};

    // Reformat data
    _.forEach(listOfMonths, (month) => {
      const data = _.get(this.raw, month, {
        days: 0,
        duration: 0,
        sessions: 0,
      });

      months[month] = {
        x: month,
        days: data.days,
        duration: data.duration,
        sessions: data.sessions,
      };
    });

    // Calculate running sum
    const orderedList = _.map(_.sortBy(months, "x"), (m) => {
      m.x = moment(m.x).format("MMM YYYY");
      m.hours = _.round(moment.duration(m.duration).asHours(), 1);
      return m;
    });
    // The last data point should be explicitly known for our "running man" icon on the graph
    const lastDataPoint = _.last(_.filter(orderedList, (s) => s.days > 0));
    if (lastDataPoint) {
      lastDataPoint.isLastDataPoint = true;
    }
    if (!aggregated) {
      return orderedList;
    }

    let runningSum = {
      days: 0,
      duration: 0,
      sessions: 0,
    };
    _.forEach(orderedList, (month) => {
      // For months with no tugging, show a gap on the chart
      if (month.days === 0 && !_.includes(this.gaps, month.x)) {
        delete month.days;
        delete month.duration;
        delete month.hours;
        delete month.sessions;
        return;
      }
      month.aggregate = {};

      runningSum.days += month.days;
      month.aggregate.days = runningSum.days;

      runningSum.duration += month.duration;
      month.aggregate.hours = _.round(
        moment.duration(runningSum.duration).asHours(),
        1
      );

      runningSum.sessions += month.sessions;
      month.aggregate.sessions = runningSum.sessions;
    });
    runningSum.hours = _.round(
      moment.duration(runningSum.duration).asHours(),
      1
    );
    return orderedList;
  }

  get total() {
    const total = new Snapshot();
    _.forEach(this.raw, (snapshot, month) => {
      if (month === "total") {
        return;
      }
      total.days += snapshot.days;
      total.duration += snapshot.duration;
      total.sessions += snapshot.sessions;

      _.mergeWith(
        total.methods,
        snapshot.methods,
        (a, b) => (a || 0) + (b || 0)
      );
    });
    return total;
  }

  track(session) {
    const month = moment(session.date)
      .startOf("month")
      .format(TimeUtils.FullDateString);

    if (!_.has(this.raw, month)) {
      this.raw[month] = new Snapshot();
    }
    this.raw[month].track(session);
  }

  get uid() {
    return this.id;
  }

  yearHasData(year) {
    return _.includes(this.years, year);
  }

  get years() {
    return _.uniq(
      _.filter(
        _.map(this.raw, (snapshot, date) => {
          if (snapshot.sessions <= 0 || date === "total") {
            return null;
          }
          return _.parseInt(_.slice(date, 0, 4).join(""));
        })
      )
    );
  }
}
